<style lang="scss" src='./SensorCommands.scss' scoped></style>
<template src="./SensorCommands.html"></template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import moment from 'moment';
import { saveSensorCommand, deleteSensorCommand, getSensorCommands, getOTAVersion, getOTABLEVersion, getSensorLogsByRequest } from '@/api/index';
import { Consts, Models } from 'gw-js-lib';

import i18n from './../../../../i18n';

const commandTypes = [ Consts.CommandType.OTA, Consts.CommandType.ENVIRONMENT, Consts.CommandType.LOGS,
                        Consts.CommandType.UPDATE_GPS, Consts.CommandType.OVERRIDE_GPS, Consts.CommandType.SET_RETRY, 
                        Consts.CommandType.NET_SWITCHING, Consts.CommandType.BLE_UPDATE, Consts.CommandType.SET_BLE_MODE ];
const OTA_url = 'https://gwx-ota.s3.us-east-1.amazonaws.com/GWX_GX-1A-${version}.bin';
const OTA_BLE_url = 'https://gwx-ota.s3.amazonaws.com/GWX_GX-BLE-${version}.bin';
extend('required', required);

export default {
  name: 'SensorCommands',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    sensor: {
      required: false,
    },
  },
  data() {
    return ({
      commandTypes: commandTypes,
      commands: [],
      versions: [],
      ble_versions: [],
      command: Models.M_sensorCommand(),
      loading: {
          sensorCommands: false,
          savingCommand: false,
          otaVersions: false,
          otaBleVersions: false,
          downloadLogs: false,
      },
      selectedOTAVersion: null,
      selectedOTABLEVersion: null,
    });
  },
  computed: {
  },
  watch: {
    sensor: function (newSensor, oldSensor) {
      if (newSensor && oldSensor && newSensor.sensor_id !== oldSensor.sensor_id) {
        this.loadData();
      }
    }
  },

  methods: {

    formatRetries() {
      if(!isNaN(this.command.attributes.netRetry)){
        this.command.attributes.netRetry = parseInt(this.command.attributes.netRetry);
        if(this.command.attributes.netRetry <= 0){
          this.command.attributes.netRetry = 1;
        }
        if(this.command.attributes.netRetry > 30){
          this.command.attributes.netRetry = 30
        }
      }else{
        this.command.attributes.netRetry = 4;
      }

      if(!isNaN(this.command.attributes.sendRetry)){
        this.command.attributes.sendRetry = parseInt(this.command.attributes.sendRetry);
        if(this.command.attributes.sendRetry <= 0){
          this.command.attributes.sendRetry = 1;
        }
        if(this.command.attributes.sendRetry > 30){
          this.command.attributes.sendRetry = 30;
        }
      }else{
        this.command.attributes.netRetry = 3;
      }
      
    },

    async loadCommands() {
      this.loading.sensorCommands = true;

      await getSensorCommands(this.sensor.sensor_id).then((commands) => {
        this.commands = commands;
      }).finally(() => {
        this.loading.sensorCommands = false;
      });
    },

    async loadOTAVersions() {
      this.loading.otaVersions = true;

      await getOTAVersion().then((versions) => {
        this.versions = versions;
      }).finally(() => {
        this.loading.otaVersions = false;
      });
    },

    async loadOTABLEVersions() {
      this.loading.otaBleVersions = true;

      await getOTABLEVersion().then((versions) => {
        this.ble_versions = versions;
      }).finally(() => {
        this.loading.otaBleVersions = false;
      });
    },

    async saveCommand() {
      const validForm = await this.$refs.validator.validate().then((v) => v);
      if (validForm) {
        this.loading.savingCommand = true;
        this.command.sensor_id = this.sensor.sensor_id;
        this.G_removeNullAttr(this.command.attributes);
        saveSensorCommand(this.command)
          .then((commandSaved) => {
            if (process.env.VUE_APP_SENTRY_ENV == "production") {
              this.$analytics.logEvent("web_sensor_commands_send");
            }
            
            this.commands.unshift(commandSaved);
            this.cleanForm();
          })
          .finally(()=>{
            this.loading.savingCommand = false;
          });
      }
    },

    async deleteCommand(command, index) {
      this.G_Alert_v2({ style: this.consts.Style.DANGER, 
                        message: i18n.t('sensorCommand.deleteCommandMsg'),
                        fnOk: () => {
                          deleteSensorCommand(command.request_id)
                          .then(() => {
                            this.commands.splice(index, 1);
                          });
                        }
                      });
    },

    onVersionChange(){
      if (this.command.attributes.url && this.selectedOTAVersion) {
        const urlArr = this.command.attributes.url.split("-");
        if (urlArr.length > 1) {
          this.command.attributes.url = this.command.attributes.url.replace(
                                                      urlArr[urlArr.length - 1],
                                                      `${this.selectedOTAVersion.version}.bin`);
        }

        this.command.attributes.version = this.selectedOTAVersion.version;
        this.command.attributes.crc = this.selectedOTAVersion.crc;
      }
    },

    onBLEVersionChange(){
      if (this.command.attributes.url && this.selectedOTABLEVersion) {
        const urlArr = this.command.attributes.url.split("-");
        if (urlArr.length > 1) {
          this.command.attributes.url = this.command.attributes.url.replace(
                                                      urlArr[urlArr.length - 1],
                                                      `${this.selectedOTABLEVersion.version}.bin`);
        }

        this.command.attributes.version = this.selectedOTABLEVersion.version;
        this.command.attributes.crc = this.selectedOTABLEVersion.crc;
      }
    },

    cleanForm() {
      this.command = Models.M_sensorCommand();
      this.selectedOTAVersion = null;
      this.selectedOTABLEVersion = null;
      this.$refs.validator.reset();
    },

    cleanCmdAttr() {
      this.command.attributes = Models.M_sensorCommand().attributes;
      this.selectedOTAVersion = null;
      this.selectedOTABLEVersion = null;
      if (this.command.command == this.consts.CommandType.OTA) {
        this.command.attributes.url = OTA_url;
      }

      if (this.command.command == this.consts.CommandType.BLE_UPDATE) {
        this.command.attributes.url = OTA_BLE_url;
      }

      if (this.command.command == this.consts.CommandType.SET_RETRY) {
        this.command.attributes.netRetry = 4;
        this.command.attributes.sendRetry = 3;
      }

      this.$refs.validator.reset();
    },

    loadData() {
      this.cleanForm();
      this.commands = [];
      if (this.sensor){
        this.loadCommands();
        this.loadOTAVersions();
        this.loadOTABLEVersions();
      }
    },

    async dowloadLogs(command, index) {
      this.loading.downloadLogs = true;

      await getSensorLogsByRequest(this.sensor.serial_number, command.request_id).then((res) => {
        if(res.rows){
          let logs = "";
          let logList = res.rows;
          logList.forEach(log => {
            let line = log.payload.log;
            logs = `${line}${logs}`; 
          });

          logs = logs.split('\n').reverse().join('');
          if (logs.includes("|")) {
            let expresion = /\w{8}\|/mg;
            let dates = logs.match(expresion);
            if (! this.G_isEmptyArr(dates)) {
              dates.forEach((dateExa) => {
                try {
                  let date = dateExa.replace("|", "");
                  date = (parseInt(`0x${date}`) * 1000);

                  date = moment(date).format("YYYY-MM-DD, h:mm:ss"); // This is the only place where we use this date format
                  logs = logs.replace(dateExa, `${date}  `);
                } catch (error) {
                  console.log('error', error)
                }
              });
            }
          }

          var a = document.createElement("a");
          var file = new Blob([logs], {type: 'text/plain'});
          a.setAttribute("href", URL.createObjectURL(file));
          a.setAttribute("download", `${this.sensor.serial_number}_req_${command.request_id}.log`);
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }).finally(() => {
        this.loading.downloadLogs = false;
      });
    },
  },

  beforeCreate() {},

  created() {},

  mounted() {
    if (process.env.VUE_APP_SENTRY_ENV == "production") {
      this.$analytics.setCurrentScreen("Web Sensors Commands");
      this.$analytics.logEvent("web_sensors_commands");
    }

    this.loadData();
  },
};
</script>
